import { Vessel } from '../../types/ViewModel/Vessel/Vessel';
import { getLuxonDateTime, isDateBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { formatAolUrlDate, getAolDateFromUrlFormat } from './aolDateUtils';

type VesselStartDateSource = Pick<Vessel, 'orcaAolStartDate' | 'orcaWebStartDate'>;

const toValidDate = (value: Date | string | null | undefined): Date | null => {
    if (!value) return null;

    const dateTime = getLuxonDateTime(value as Date);
    return dateTime?.isValid ? dateTime.toJSDate() : null;
};

/**
 * The date a vessel became active in our systems, as maintained in Armada.
 * Uses the earliest of the dedicated AOL start date and the general OrcaWeb start date.
 * Returns null when neither is set, which means no restriction applies.
 */
export const getEffectiveAolStartDate = (vessel: VesselStartDateSource | null | undefined): Date | null => {
    if (!vessel) return null;

    const aolStartDate = toValidDate(vessel.orcaAolStartDate);
    const webStartDate = toValidDate(vessel.orcaWebStartDate);

    if (!aolStartDate) return webStartDate;
    if (!webStartDate) return aolStartDate;

    return isDateBefore(aolStartDate, webStartDate, 'day') ? aolStartDate : webStartDate;
};

export const isBeforeAolStartDate = (date: Date | string | null | undefined, vessel: VesselStartDateSource | null | undefined): boolean => {
    const startDate = getEffectiveAolStartDate(vessel);
    const candidate = toValidDate(date);

    if (!startDate || !candidate) return false;

    return isDateBefore(candidate, startDate, 'day');
};

/** Raises fromDate to the vessel's effective AOL start date so no period before it can be requested. */
export const clampAolFromDate = <T extends Date | null | undefined>(fromDate: T, vessel: VesselStartDateSource | null | undefined): T | Date => {
    const startDate = getEffectiveAolStartDate(vessel);
    if (!startDate || !fromDate) return fromDate;

    return isDateBefore(fromDate, startDate, 'day') ? startDate : fromDate;
};

export const clampAolUrlFromDate = (fromDate: string, vessel: VesselStartDateSource | null | undefined): string =>
    formatAolUrlDate(clampAolFromDate(getAolDateFromUrlFormat(fromDate), vessel) as Date);

/**
 * Clamps a from/to range to the vessel's effective AOL start date.
 * When the whole range predates the start date the range collapses onto the start date
 * rather than inverting.
 */
export const clampAolDateRange = (
    fromDate: Date,
    toDate: Date,
    vessel: VesselStartDateSource | null | undefined
): { fromDate: Date; toDate: Date; isClamped: boolean } => {
    const startDate = getEffectiveAolStartDate(vessel);

    if (!startDate || !fromDate || !isDateBefore(fromDate, startDate, 'day')) {
        return { fromDate, toDate, isClamped: false };
    }

    const clampedToDate = toDate && isDateBefore(toDate, startDate, 'day') ? startDate : toDate;

    return { fromDate: startDate, toDate: clampedToDate, isClamped: true };
};
